import _ from 'lodash';
import {
  ecommerceAddToCartEvent,
  ecommerceSelectItemEvent,
  ecommerceViewItemEvent,
  esputnikAddToCart,
  generateVisitorId,
  getCookie,
  getProductPriceTitle
} from '@/helpers';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import IconMinus from '@/components/icons/IconMinus.vue';
import IconPlus from '@/components/icons/IconPlus.vue';
import StarRating from 'vue-star-rating';
import { MEDIA, pluralizate } from '@/helpers/variables';
import { debounce } from 'lodash';
import IconInfo from '@/components/icons/IconInfo.vue';
import moment from 'moment';

export default {
  name: 'ProductInfo',
  components: { IconInfo, IconPlus, IconMinus, StarRating },
  props: {
    productData: {
      type: Object,
      default: () => {}
    },
    seriesProducts: {
      type: Array,
      default: () => []
    }
  },
  emits: ['change-selected-price'],
  data() {
    return {
      MEDIA,
      selectedCount: 1,
      selectedProductPrice: null,
      isLoadingSyncProductPrice: false,
      promoTimer: null,
      promoTimeleft: {
        total: 0,
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0
      },
      endTime: '2025-12-01 00:00:00'
    };
  },
  created() {
    this._checkValue();

    let { productPrices, title } = this.productData;

    ecommerceViewItemEvent({
      item_id: productPrices.data[0].id,
      item_name: title,
      item_brand: this.productData.brandSeries?.title || 'Renew',
      item_category: this.productData.brand?.title || '',
      item_variant: productPrices.data[0].title,
      price: productPrices.data[0].price
    });
  },
  computed: {
    ...mapGetters(['currentLanguage']),
    ...mapGetters({
      isUserWithCode: 'profile/isUserWithCode',
      user: 'profile/user',
      userEmailByVisitorUUID: 'profile/userEmailByVisitorUUID',
      basket: 'basket/basket',
      productPriceByCount: 'products/productPriceByCount',
      showNotifyLoader: 'products/showNotifyLoader',
      appSize: 'app/size'
    }),
    productCount() {
      return this.selectedProductPrice?.quantity;
    },
    leftoverType() {
      if (this.selectedProductPrice) {
        let { leftoverType } = this.selectedProductPrice;
        return leftoverType.data;
      }
      return null;
    },
    isWithoutBox() {
      return this.selectedProductPrice?.withoutBox;
    },
    isOutlet() {
      return this.selectedProductPrice?.outletReasons?.data?.length;
    },
    isRefill() {
      return this.selectedProductPrice?.isRefill;
    },
    isHasDuplicate() {
      return this.productData.hasDuplicate;
    },
    isOutStock() {
      return this.leftoverType && this.leftoverType.displayType === 'out-stock';
    },
    isProfessionalOnly() {
      return this.selectedProductPrice && this.selectedProductPrice.showWithoutPrice;
    },
    isCanBePurchased() {
      return !this.isOutStock && !this.isHasDuplicate && !this.isProfessionalOnly;
    },
    isDiscontinued() {
      return this.productData.discontinued;
    },
    isHasAnalogue() {
      return this.productData.analogueProduct && this.productData.analogueProduct.data;
    },
    isComingSoon() {
      return this.productData.isComingSoon;
    },
    isShowOldPrice() {
      if (!this.selectedProductPrice) return false;
      let { hasActivePromotion, oldPrice } = this.selectedProductPrice;
      return hasActivePromotion || oldPrice;
    },

    professionalLink() {
      let key = this.currentLanguage?.key ? this.currentLanguage.key + '/' : '';
      let { redirectTo } = this.selectedProductPrice;
      return redirectTo || `${process.env.VUE_APP_URL_PROFESSIONAL}${key}catalog?brands=renew`;
    },
    giftProduct() {
      return this.productData.giftProduct?.data || null;
    },
    commentsCount() {
      return this.productData.feedbacks?.data?.length || 0;
    },
    commentsPluralizate() {
      return pluralizate(
        this.commentsCount,
        this.$t('label.comments.countOne'),
        this.$t('label.comments.countSimple'),
        this.$t('label.comments.countMany')
      );
    },
    outlets() {
      return this.selectedProductPrice?.outletReasons?.data || [];
    },
    formattedTime() {
      if (this.promoTimeleft.total <= 0) return '00:00:00';
      let { hours, minutes, seconds } = this.promoTimeleft;
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    },
    promoDaysPluralizate() {
      return pluralizate(
        this.promoTimeleft?.days || 0,
        this.$t('label.days.countOne'),
        this.$t('label.days.countSimple'),
        this.$t('label.days.countMany')
      );
    }
  },
  methods: {
    ...mapActions({
      getProduct: 'products/GET_PRODUCT',
      showNotifyPopup: 'products/SHOW_POPUP_PRODUCT_WHEN_AVAILABLE',
      getUserEmailByVisitorUUID: 'profile/GET_USER_EMAIL_BY_VISITOR_UUID'
    }),
    ...mapMutations({
      addToBasket: `basket/ADD_ITEM_TO_BASKET`,
      modalOpen: 'app/MODAL_OPEN'
    }),
    getProductPriceTitle,
    _calcPromoTimeleft(endTime) {
      const end = moment(endTime);
      const now = moment();
      const diff = end.diff(now);

      if (diff <= 0) {
        return { total: 0, days: 0, hours: 0, minutes: 0, seconds: 0 };
      }

      const duration = moment.duration(diff);

      return {
        total: diff,
        days: Math.floor(duration.asDays()),
        hours: duration.hours(),
        minutes: duration.minutes(),
        seconds: duration.seconds()
      };
    },
    _checkValue() {
      const productPrices = this.productData.productPrices.data;

      const productPrice = _.find(productPrices, (item) => this.isSelectedPrice(item));

      this.onSelectProductPrice(productPrice || productPrices[0]);
    },
    _setSelectedCount(count, productPrice = null) {
      let setQuantity = Math.min(
        Math.max(Number.parseFloat(count || '1'), 1),
        (productPrice || this.selectedProductPrice).quantity
      );

      this.selectedCount = Math.ceil(setQuantity);
      this._updateProductPrices(this.selectedCount);
    },

    _updateProductPrices: debounce(async function (count) {
      this.isLoadingSyncProductPrice = true;
      await this.getProduct({
        slug: this.$route.params.slug,
        value: this.selectedProductPrice.value,
        count: count
      });
      this.isLoadingSyncProductPrice = false;
    }, 300),

    isSelectedPrice(productPrice) {
      if (this.selectedProductPrice) {
        let isSameValue = parseInt(this.selectedProductPrice.value) === parseInt(productPrice.value);
        let isSameId = this.selectedProductPrice.id === productPrice.id;
        return isSameValue && isSameId;
      }
      return false;
    },
    onSelectProductPrice(productPrice) {
      this.selectedProductPrice = productPrice;
      this._setSelectedCount(this.selectedCount, productPrice);

      if (this.promoTimer) {
        clearInterval(this.promoTimer);
        this.promoTimer = null;
      }

      if (productPrice.promotion?.activities?.data.length) {
        this.promoTimer = setInterval(() => {
          let endDate = productPrice.promotion?.activities?.data[0].dateTo;
          this.promoTimeleft = this._calcPromoTimeleft(endDate);
        }, 1000);
      }

      this.$emit('change-selected-price', this.selectedProductPrice);

      ecommerceSelectItemEvent({
        item_id: this.selectedProductPrice.id,
        item_name: this.productData.title,
        item_brand: this.productData.brandSeries?.title || 'Renew',
        item_category: this.productData.brand?.title || '',
        item_variant: this.selectedProductPrice.title,
        price: this.selectedProductPrice.price
      });
    },
    onChangeCount(type, count) {
      if (type === 'decrement' && this.selectedCount > 1) {
        this._setSelectedCount(this.selectedCount - 1);
      } else if (type === 'increment') {
        this._setSelectedCount(this.selectedCount + 1);
      } else {
        this._setSelectedCount(count);
      }
    },
    async onAddBasket() {
      this.addToBasket({
        select_count: this.selectedCount,
        product_price: this.selectedProductPrice,
        product: this.productData,
        unauthorizedProductPriceId: this.productData.unauthorizedProductPriceId
      });

      ecommerceAddToCartEvent({
        value: (this.selectedCount * this.selectedProductPrice.price).toFixed(2),
        item_id: this.selectedProductPrice.id,
        item_name: this.productData.title,
        item_brand: this.productData.brandSeries?.title || 'Renew',
        item_category: this.productData.brand?.title || '',
        item_variant: this.selectedProductPrice.title,
        price: this.selectedProductPrice.price,
        quantity: this.selectedCount,
        content_ids: this.productData.unauthorizedProductPriceId
      });

      //---ESputnik

      let visitorId = getCookie('visitorUUID');
      if (!visitorId) {
        visitorId = generateVisitorId();
      }

      let esputnikPayload = {
        keyValue: visitorId,
        params: []
      };

      esputnikPayload.params.push({
        name: 'externalCustomerId',
        value: visitorId
      });

      let userEmail = this.user?.email || this.userEmailByVisitorUUID;

      if (!userEmail) {
        await this.getUserEmailByVisitorUUID();
        userEmail = this.userEmailByVisitorUUID;
      }
      if (userEmail) {
        esputnikPayload = { ...esputnikPayload, keyValue: userEmail };
        esputnikPayload.params.push({
          name: 'EmailAddress',
          value: userEmail
        });
      }

      let { uaTitle, image, brand } = this.productData;

      esputnikPayload.params.push(
        {
          name: 'productName',
          value: uaTitle
        },
        {
          name: 'categoryName',
          value: brand ? brand.title : ''
        },
        {
          name: 'Price',
          value: this.selectedProductPrice.price
        },
        {
          name: 'URL',
          value: window.location.href
        },
        {
          name: 'imageURL',
          value: image
        }
      );

      if (this.seriesProducts.length) {
        let seriesProducts = this.seriesProducts.slice(0, 2);
        seriesProducts.forEach((item, index) => {
          let { uaTitle, productPrices, image, slug, brand } = item;

          let langPath = this.$route.params.lang ? `${this.$route.params.lang}/` : '';
          let productPath = `katalog/${brand.slug}/${slug}/`;
          let similarUrl = `${window.location.origin}/${langPath}${productPath}`;

          esputnikPayload.params.push(
            {
              name: `similar${index + 1}name`,
              value: uaTitle
            },
            {
              name: `similar${index + 1}price`,
              value: productPrices && productPrices.data.length ? productPrices.data[0].price : ''
            },
            {
              name: `similar${index + 1}URL`,
              value: similarUrl
            },
            {
              name: `similar${index + 1}image`,
              value: image
            }
          );
        });
      }

      esputnikAddToCart(esputnikPayload);

      this.modalOpen('basketModal');
      this.$toasted.success(this.$t('toast.successAddBasket'));
    },

    onShowNotify() {
      const payload = {
        product_id: this.productData.id,
        product_price_id: this.selectedProductPrice.id
      };

      this.showNotifyPopup(payload);
    }
  },
  beforeDestroy() {
    clearInterval(this.promoTimer);
  }
};
